import { createApi, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { apiPath, baseUrl } from 'config'

import { getSignature } from 'utils/getSignature'

import {
  ApproveWithdrawalRequestBody,
  CreateWithdrawalRequestBody,
  GetLeaderCashflowsParams,
  LeaderCashflowsResponse,
  LeaderInvestorsResponse,
  LeaderProfile,
  ReferralLeadersResponse,
  UpdateRefLeaderProfileBody,
  UpdateRefLeaderProfileResponse,
} from './type'

export const queryReferrals = createApi({
  baseQuery: fetchBaseQuery({ baseUrl }),
  endpoints: (builder) => ({
    approveWithdrawalRequest: builder.mutation<void, ApproveWithdrawalRequestBody>({
      invalidatesTags: ['Cashflows'],
      async queryFn(body, _api, _extraOptions, baseQuery) {
        const url = `${apiPath.referrals}/request/${body.id}/approve`

        const { deadline, signature } = await getSignature({
          payload: { additionalInfo: body.additionalInfo },
          primaryType: 'ApproveWithdrawalRequest',
          url,
        })

        if (!signature) {
          return { error: new Error('No signature') as unknown as FetchBaseQueryError }
        }

        const result = await baseQuery({
          body: { additionalInfo: body.additionalInfo },
          headers: { deadline: `${deadline}`, signature },
          method: 'POST',
          url,
        })

        if (result.error) {
          return { error: result.error as FetchBaseQueryError }
        }

        return { data: undefined }
      },
    }),

    cancelWithdrawalRequest: builder.mutation<void, string>({
      invalidatesTags: ['Cashflows'],
      queryFn: async (id, _api, _extraOptions, baseQuery) => {
        const url = `${apiPath.referrals}/request/${id}`

        const { deadline, signature } = await getSignature({
          primaryType: 'CancelWithdrawalRequest',
          url,
        })

        if (!signature) {
          return { error: new Error('No signature') as unknown as FetchBaseQueryError }
        }

        const result = await baseQuery({
          headers: { deadline: `${deadline}`, signature },
          method: 'DELETE',
          url,
        })

        if (result.error) {
          return { error: result.error as FetchBaseQueryError }
        }

        return { data: undefined }
      },
    }),

    createWithdrawalRequest: builder.mutation<void, CreateWithdrawalRequestBody>({
      invalidatesTags: ['Cashflows'],
      async queryFn(body, _api, _extraOptions, baseQuery) {
        const url = `${apiPath.referrals}/request`
        const amountStr = String(body.amount)

        const { deadline, signature } = await getSignature({
          payload: { amount: amountStr },
          primaryType: 'CreateWithdrawalRequest',
          url,
        })

        if (!signature) {
          return { error: new Error('No signature') as unknown as FetchBaseQueryError }
        }

        const result = await baseQuery({
          body: { amount: amountStr },
          headers: { deadline: `${deadline}`, signature },
          method: 'POST',
          url,
        })

        if (result.error) {
          return { error: result.error as FetchBaseQueryError }
        }

        return { data: undefined }
      },
    }),

    getAllReferralLeaders: builder.query<ReferralLeadersResponse, { limit: number; page: number }>({
      providesTags: ['AllReferralLeaders'],
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const url = `${apiPath.referrals}?limit=${arg.limit}&page=${arg.page}`

        const { deadline, signature } = await getSignature({
          primaryType: 'GetAllReferralLeaders',
          url,
        })

        if (!signature) {
          return { error: new Error('No signature') as unknown as FetchBaseQueryError }
        }

        const result = await baseQuery({
          headers: { deadline: `${deadline}`, signature },
          url,
        })

        if (!result.data) {
          return { error: result.error as FetchBaseQueryError }
        }

        return { data: result.data as ReferralLeadersResponse }
      },
    }),

    getLeaderCashflows: builder.query<LeaderCashflowsResponse, GetLeaderCashflowsParams>({
      providesTags: ['Cashflows'],
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const params = new URLSearchParams({ limit: String(arg.limit), page: String(arg.page) })
        if (arg.type) params.set('type', arg.type)
        if (arg.status) params.set('status', arg.status)
        if (arg.address) params.set('address', arg.address)

        const url = `${apiPath.referrals}/cashflows?${params.toString()}`

        const { deadline, signature } = await getSignature({
          primaryType: 'GetLeaderCashflows',
          url,
        })

        if (!signature) {
          return { error: new Error('No signature') as unknown as FetchBaseQueryError }
        }

        const result = await baseQuery({
          headers: { deadline: `${deadline}`, signature },
          url,
        })

        if (!result.data) {
          return { error: result.error as FetchBaseQueryError }
        }

        return { data: result.data as LeaderCashflowsResponse }
      },
    }),

    getLeaderInvestors: builder.query<LeaderInvestorsResponse, { address: string; limit: number; page: number }>({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const url = `${apiPath.referrals}/${arg.address}/investors?limit=${arg.limit}&page=${arg.page}`

        const { deadline, signature } = await getSignature({
          primaryType: 'GetLeaderInvestors',
          url,
        })

        if (!signature) {
          return { error: new Error('No signature') as unknown as FetchBaseQueryError }
        }

        const result = await baseQuery({
          headers: { deadline: `${deadline}`, signature },
          url,
        })

        if (!result.data) {
          return { error: result.error as FetchBaseQueryError }
        }

        return { data: result.data as LeaderInvestorsResponse }
      },
    }),

    getLeaderProfile: builder.query<LeaderProfile, { address?: string }>({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const params = new URLSearchParams()
        if (arg.address) params.set('address', arg.address)

        const query = params.toString()
        const url = `${apiPath.referrals}/profile${query ? `?${query}` : ''}`

        const { deadline, signature } = await getSignature({
          primaryType: 'GetLeaderProfile',
          url,
        })

        if (!signature) {
          return { error: new Error('No signature') as unknown as FetchBaseQueryError }
        }

        const result = await baseQuery({
          headers: { deadline: `${deadline}`, signature },
          url,
        })

        if (!result.data) {
          return { error: result.error as FetchBaseQueryError }
        }

        return { data: result.data as LeaderProfile }
      },
    }),

    updateRefLeaderProfile: builder.mutation<UpdateRefLeaderProfileResponse, UpdateRefLeaderProfileBody>({
      invalidatesTags: ['AllReferralLeaders'],
      async queryFn(body, _api, _extraOptions, baseQuery) {
        const url = `${apiPath.referrals}/profile`
        const revenueShareStr = String(body.revenueShare)

        const { deadline, signature } = await getSignature({
          payload: {
            address: body.address,
            name: body.name,
            revenueShare: revenueShareStr,
          },
          primaryType: 'UpdateRefLeaderProfile',
          url,
        })

        if (!signature) {
          return { error: new Error('No signature') as unknown as FetchBaseQueryError }
        }

        const result = await baseQuery({
          body: { address: body.address, name: body.name, revenueShare: revenueShareStr },
          headers: { deadline: `${deadline}`, signature },
          method: 'POST',
          url,
        })

        if (result.error) {
          return { error: result.error as FetchBaseQueryError }
        }

        return { data: result.data as UpdateRefLeaderProfileResponse }
      },
    }),
  }),
  reducerPath: 'referrals',
  tagTypes: ['AllReferralLeaders', 'Cashflows'],
})

export const {
  useApproveWithdrawalRequestMutation,
  useCancelWithdrawalRequestMutation,
  useCreateWithdrawalRequestMutation,
  useGetAllReferralLeadersQuery,
  useGetLeaderCashflowsQuery,
  useGetLeaderInvestorsQuery,
  useGetLeaderProfileQuery,
  useUpdateRefLeaderProfileMutation,
} = queryReferrals
